import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useEffectOnce } from 'react-use';
import { connect } from 'react-redux';
import { useCallback } from 'react';

import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IDictionaryRequirementSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRequirementSelectOption';
import { useForceUpdate } from 'CorePackage/MainModule/hooks/useForceUpdate';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmIsEditableInStatusCommentsExist,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { NEW_REQUIREMENT_SELECT_OPTION_CODE } from '../constants/newRequirementSelectOption';
import { observedSelectOptions } from '../constants/observedSelectOptions';
import { IKnmErknmFormRequirement } from '../interfaces/IKnmErknmFormRequirement';
import styles from './ErknmRequirementsTable.module.scss';
import { requiredRequirementsErknmOrApproveDocsOrLinks } from '../../../CorePackage/MainModule/validators/requiredRequirementsErknmOrApproveDocsOrLinks';

const REQUIREMENTS_SELECT_OPTIONS_MAX_VISIBLE_NUMBER = 25;

interface IErknmRequirementsTableProps {
  className?: string;
  watchOnly: boolean;
  formName: string;
  requirementsSelectOptions?: IDictionaryRequirementSelectOption[];
  isEnabled?: boolean;
  makeTitleSelectChangeHandler?: (fieldName: string) => (value: IDictionaryRequirementSelectOption | undefined) => void;
  getItemBlockId?: (id: string) => string;
}

interface IErknmRequirementsTableStoreProps {
  isSupervisor: boolean | undefined;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  formRef: IKnmFormRef | undefined;
  isAvailableInStatusCommentsExist: boolean;
}

const ErknmRequirementsTableWithoutStore = ({
  className,
  watchOnly,
  formName,
  requirementsSelectOptions,
  isEnabled,
  makeTitleSelectChangeHandler,
  getItemBlockId,
  formRef,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  isSupervisor,
  isAvailableInStatusCommentsExist,
}: IErknmRequirementsTableProps & IErknmRequirementsTableStoreProps) => {
  const remove = formRef?.current.mutators.remove;

  const makeRemoveField = (index: number) => () => remove && remove(formName, index);

  const forceUpdate = useForceUpdate();

  useEffectOnce(() => {
    forceUpdate();
  });

  const isLoadTitleSelectOptionsActive =
    requirementsSelectOptions && requirementsSelectOptions?.length > REQUIREMENTS_SELECT_OPTIONS_MAX_VISIBLE_NUMBER;

  const filterRequirementsSelectOptions = useCallback(
    (inputValue: string) => {
      const filteredRequirementsSelectOptions: IDictionaryRequirementSelectOption[] = [];

      requirementsSelectOptions?.some(option => {
        if (option.label.toLowerCase().includes(inputValue.toLowerCase())) {
          filteredRequirementsSelectOptions.push(option);
          if (filteredRequirementsSelectOptions.length >= REQUIREMENTS_SELECT_OPTIONS_MAX_VISIBLE_NUMBER) {
            return true; // break;
          }
        }

        return false;
      });
      const newOption = requirementsSelectOptions?.find(item => item.code === NEW_REQUIREMENT_SELECT_OPTION_CODE);

      if (
        !filteredRequirementsSelectOptions?.find(item => item.code === NEW_REQUIREMENT_SELECT_OPTION_CODE) &&
        newOption
      ) {
        filteredRequirementsSelectOptions.push(newOption);
      }

      return filteredRequirementsSelectOptions;
    },
    [requirementsSelectOptions],
  );

  const loadTitleSelectOptions = useCallback(
    (inputValue: string, callback: (options: IDictionaryRequirementSelectOption[]) => void) => {
      setTimeout(() => {
        callback(filterRequirementsSelectOptions(inputValue));
      }, 300);
    },
    [filterRequirementsSelectOptions],
  );

  return (
    <FieldArray
      name={formName}
      validate={isAvailableInStatusCommentsExist ? requiredRequirementsErknmOrApproveDocsOrLinks : () => undefined}
      key={isAvailableInStatusCommentsExist ? 0 : 1}
    >
      {({ fields }: FieldArrayRenderProps<IKnmErknmFormRequirement, HTMLElement>) => {
        if (!fields.length) {
          return null;
        }

        return (
          <table className={className ? `${styles.RequirementsTable} ${className}` : styles.RequirementsTable}>
            <thead className={styles.RequirementsThead}>
              <tr className={styles.RequirementsTableRow}>
                <th className={styles.NumberTheadCell}>№</th>
                <th className={styles.NameTheadCell}>Формулировка требования</th>
                <th className={styles.NameNpaTheadCell}>Наименование НПА</th>
                <th className={styles.DateNpaTheadCell}>Дата НПА</th>
                <th className={styles.ObservedTheadCell} />
                {!watchOnly && <th className={styles.CloseTheadCell} />}
              </tr>
            </thead>
            <tbody>
              {fields.map((fieldName: string, index: number) => {
                const fieldValue: IKnmErknmFormRequirement = fields.value?.[index];
                const id = getItemBlockId ? getItemBlockId(fieldValue.listId) : `${fieldName}[${index}]`;
                const isNewRequirement = fieldValue.requirement?.code === NEW_REQUIREMENT_SELECT_OPTION_CODE;
                const isObservedFieldEnabled =
                  !watchOnly && isSupervisor && (isKnmStatusTypeCarriedOut || isKnmStatusTypeCompleted);

                return (
                  <tr className={styles.RequirementsTableRow} id={id} key={id}>
                    <td className={styles.NumberTbodyCell}>{index + 1}</td>
                    <td className={styles.NameTbodyCell}>
                      <SelectInputField
                        name={`${fieldName}.requirement`}
                        id={`${fieldName}.requirement`}
                        size="medium"
                        isAsync
                        isSearchable
                        defaultOptions={
                          isLoadTitleSelectOptionsActive
                            ? filterRequirementsSelectOptions('')
                            : requirementsSelectOptions
                        }
                        placeholder="Обязательное требование"
                        loadingMessage="Поиск..."
                        disabled={!isEnabled && !fieldValue.isNew}
                        onChange={makeTitleSelectChangeHandler ? makeTitleSelectChangeHandler(fieldName) : undefined}
                        loadOptions={loadTitleSelectOptions}
                      />
                      {isNewRequirement && (
                        <TextareaField
                          className={styles.TitleField}
                          id={`${fieldName}.manualTitle`}
                          name={`${fieldName}.manualTitle`}
                          placeholder="Введите наименование обязательного требования"
                          resize="none"
                          disabled={!isEnabled && !fieldValue.isNew}
                        />
                      )}
                    </td>
                    <td className={styles.NameNpaTbodyCell}>
                      <TextareaField
                        fieldKey={isNewRequirement ? '1' : '0'}
                        className={styles.NameNpaField}
                        id={`${fieldName}.manualNameNpa`}
                        name={`${fieldName}.manualNameNpa`}
                        placeholder={isNewRequirement ? 'Введите наименование НПА' : 'Наименование НПА'}
                        resize="none"
                        disabled={!isNewRequirement}
                      />
                    </td>
                    <td className={styles.DateNpaTbodyCell}>
                      <DatePickerField
                        className={styles.DateNpaField}
                        id={`${fieldName}.manualDateNpa`}
                        name={`${fieldName}.manualDateNpa`}
                        disabled={!isNewRequirement}
                      />
                    </td>
                    <td className={styles.ObservedTbodyCell}>
                      <SelectInputField
                        className={styles.ObservedField}
                        id={`${fieldName}.isObserved`}
                        name={`${fieldName}.isObserved`}
                        placeholder="Выберите соблюдение требования"
                        options={observedSelectOptions}
                        size="medium"
                        disabled={!isObservedFieldEnabled}
                        isClearable={false}
                      />
                      {fieldValue?.isObserved?.value === false && (!watchOnly || (watchOnly && fieldValue?.note)) && (
                        <TextareaField
                          className={styles.NoteField}
                          id={`${fieldName}.note`}
                          name={`${fieldName}.note`}
                          placeholder="Введите комментарий"
                          disabled={watchOnly}
                        />
                      )}
                    </td>
                    {isEnabled && (
                      <td className={styles.NumberTbodyCell}>
                        <CloseButton className={styles.DeleteButton} onClick={makeRemoveField(index)} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      }}
    </FieldArray>
  );
};

const mapStateToProps = (state: IRootState): IErknmRequirementsTableStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  formRef: getKnmFormRef(state),
  isAvailableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
});

export const ErknmRequirementsTable = connect(mapStateToProps)(ErknmRequirementsTableWithoutStore);
